import { getPrerenderWidth } from "@/helpers/prerenderWidth"
import { useHydrated } from "@/hooks/useHydrated"
import { useMediaQuery } from "react-responsive"

enum Breakpoints {
  XXS = 480,
  XS = 575,
  SM = 576,
  MD = 768,
  LG = 992,
  XL = 1200,
  XXL = 1600,
}

type SizeMap = Readonly<{ [key in keyof typeof Breakpoints]: Breakpoints }>

const sizes: SizeMap = {
  XXS: Breakpoints.XXS,
  XS: Breakpoints.XS,
  SM: Breakpoints.SM,
  MD: Breakpoints.MD,
  LG: Breakpoints.LG,
  XL: Breakpoints.XL,
  XXL: Breakpoints.XXL,
} as const

type DeviceType = "xxs" | "xs" | "sm" | "md" | "lg" | "xl" | "xxl"

type BreakpointHook = { [key in `is${Capitalize<DeviceType>}`]: boolean } & { sizes: SizeMap }

/** The same ranges as the media queries below, for a known width. */
const breakpointsForWidth = (width: number): BreakpointHook => ({
  isXxs: width <= Breakpoints.XXS,
  isXs: width <= Breakpoints.SM - 1,
  isSm: width >= Breakpoints.SM && width <= Breakpoints.MD - 1,
  isMd: width >= Breakpoints.MD && width <= Breakpoints.LG - 1,
  isLg: width >= Breakpoints.LG && width <= Breakpoints.XL - 1,
  isXl: width >= Breakpoints.XL && width <= Breakpoints.XXL - 1,
  isXxl: width >= Breakpoints.XXL,
  sizes,
})

/**
 * The window's breakpoints. On the server and while a prerendered page hydrates, the breakpoints of the width the page
 * was rendered at instead, so the browser's first render matches the HTML; the real ones follow right after.
 */
function useBreakpoints(): BreakpointHook {
  const live: BreakpointHook = {
    isXxs: useMediaQuery({
      maxWidth: Breakpoints.XXS,
    }),

    isXs: useMediaQuery({
      maxWidth: Breakpoints.SM - 1,
    }),

    isSm: useMediaQuery({
      minWidth: Breakpoints.SM,
      maxWidth: Breakpoints.MD - 1,
    }),

    isMd: useMediaQuery({
      minWidth: Breakpoints.MD,
      maxWidth: Breakpoints.LG - 1,
    }),

    isLg: useMediaQuery({
      minWidth: Breakpoints.LG,
      maxWidth: Breakpoints.XL - 1,
    }),

    isXl: useMediaQuery({
      minWidth: Breakpoints.XL,
      maxWidth: Breakpoints.XXL - 1,
    }),
    isXxl: useMediaQuery({ minWidth: Breakpoints.XXL }),
    sizes,
  }
  return useHydrated() ? live : breakpointsForWidth(getPrerenderWidth())
}

export { Breakpoints, breakpointsForWidth, useBreakpoints }
